/**
 * Reads the post-generation metadata evidence that already rides to the client
 * inside `content.generation_metadata` (postgen_metadata_async / postgen_metadata
 * / postgen_social) and normalises it into per-field state the rail can render.
 *
 * This is the "E0 accessor" — zero backend: the data is already on the wire.
 *
 * ⚠ INTERFACE IS LOCKED (agreed contract for the parallel GLM sessions).
 * Sibling sessions import `MetadataFieldState`, `MetadataRunSummary`, and
 * `readPostgenEvidence` against exactly these shapes — do NOT change the
 * exported signatures/types.
 *
 * Source-derivation rule (locked):
 *   skipped_user_edited[field] === true            → 'user-edited'  (review=false)
 *   per_field[field].fallback_fired === true       → 'fallback'     (review=true )
 *   OR field present in per_field on a failed run  → 'fallback'     (review=true )
 *   per_field[field].applied === true (not fb)     → 'ai'           (review=false)
 *   otherwise                                       → field omitted  (chip=null  )
 *
 * The parser is defensive throughout: `generation_metadata` is typed as
 * `{qa_noindex?, postgen_metadata_async?} & Record<string, unknown>`, so every
 * extension key (`per_field`, `baseline_metadata`, `postgen_metadata`,
 * `postgen_social`, …) is read through `unknown` + type guards. A missing or
 * malformed blob never throws — it yields an empty summary.
 */
import type { ContentDetail } from '@/components/BlogCreationCommandCenter/types';

/** Categorical provenance of a metadata field. Never a confidence score. */
export type FieldSource = 'ai' | 'fallback' | 'user-edited' | 'not-generated';

export interface MetadataFieldState {
  /** metadataPatch.ts field name, e.g. 'meta_title', 'meta_description', 'og_title'. */
  field: string;
  /** Categorical source — drives the provenance chip. */
  source: FieldSource;
  /** True when this field fell back to a template default or its run failed → "needs review". */
  needsReview: boolean;
  /** Pre-run value for one-click revert, when the evidence carries a baseline. */
  baseline?: string | null;
}

export interface MetadataRunSummary {
  /** Mirrors content.metadata_generation_status. */
  status: 'queued' | 'running' | 'applied' | 'failed' | null;
  /** Count of fields with needsReview=true (fallback or failed) — the rollup number. */
  needsReviewCount: number;
  /** Fields the last run wrote. */
  updatedFields: string[];
  /** Fields the run skipped because the human had edited them (skipped_user_edited). */
  keptFields: string[];
  /** Per-field state keyed by metadataPatch field name. */
  fieldStates: Record<string, MetadataFieldState>;
}

const EMPTY_SUMMARY: MetadataRunSummary = {
  status: null,
  needsReviewCount: 0,
  updatedFields: [],
  keptFields: [],
  fieldStates: {},
};

/**
 * The full metadataPatch field set (see metadataPatch.ts). We iterate this so
 * every chip-eligible field gets a stable slot in `fieldStates`; fields with no
 * evidence are simply omitted per the source-derivation rule.
 */
const POSTGEN_FIELDS: readonly string[] = [
  'meta_title',
  'meta_description',
  'url_slug',
  'excerpt',
  'og_title',
  'og_description',
  'twitter_title',
  'twitter_description',
  'canonical_url',
  'robots_override',
  'wordpress_author_id',
  'schema_types_injected',
] as const;

/**
 * Family B (social) fields — og/twitter. These are the fields whose attribution
 * becomes unknowable when the social packet has been edited as a whole (see the
 * Family B honesty rule in readPostgenEvidence).
 */
const FAMILY_B_FIELDS: ReadonlySet<string> = new Set([
  'og_title',
  'og_description',
  'twitter_title',
  'twitter_description',
]);

/**
 * Drift-detection map: postgen field → same-named property on ContentDetail
 * that holds the field's CURRENT value in the editor. Only the free-text fields
 * listed in the locked contract are eligible (url_slug, canonical_url,
 * robots_override, wordpress_author_id, schema_types_injected are structural /
 * non-string and excluded). `title` is included because content_service may
 * stash the AI-written meta_title against either property.
 */
const DRIFT_CHECKABLE: Record<string, string> = {
  meta_title: 'meta_title',
  meta_description: 'meta_description',
  excerpt: 'excerpt',
  og_title: 'og_title',
  og_description: 'og_description',
  twitter_title: 'twitter_title',
  twitter_description: 'twitter_description',
  // Family F (postgen_slug.py) writes per_field.url_slug — see the slugRec
  // reader below. A human rewriting an AI-authored slug must label "Your
  // edit", never "AI".
  url_slug: 'url_slug',
};

type RunStatus = MetadataRunSummary['status'];

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function readStatus(v: unknown): RunStatus {
  if (v === 'queued' || v === 'running' || v === 'applied' || v === 'failed') return v;
  return null;
}

/** Set of field names whose value is truthy in a `Record<field, boolean>`-ish blob. */
function truthyFieldSet(v: unknown): Set<string> {
  const out = new Set<string>();
  if (!isRecord(v)) return out;
  for (const [field, val] of Object.entries(v)) {
    if (val === true || val === 1 || val === 'true') out.add(field);
  }
  return out;
}

interface NormalisedPerField {
  applied: boolean;
  fallback_fired: boolean;
}

/** Per-field evidence map; tolerant of partial / oddly-shaped entries. */
function perFieldMap(v: unknown): Map<string, NormalisedPerField> {
  const out = new Map<string, NormalisedPerField>();
  if (!isRecord(v)) return out;
  for (const [field, raw] of Object.entries(v)) {
    if (!isRecord(raw)) continue;
    out.set(field, {
      applied: raw.applied === true,
      fallback_fired: raw.fallback_fired === true,
    });
  }
  return out;
}

function baselineString(baseline: Record<string, unknown>, field: string): string | null | undefined {
  if (!(field in baseline)) return undefined;
  const v = baseline[field];
  if (v === null) return null;
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

/** Coerce an unknown written/current value to a comparable string. */
function normalizeScalar(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return undefined;
}

/**
 * Read the value the run actually WROTE for `field`, defensively. The evidence
 * blobs store written values in a few possible shapes — `fields` map on either
 * the async or inline blob, or the value stashed directly under the field name.
 * Inspect each in priority order; return undefined when no written-value record
 * exists (in which case the drift rule is a no-op and the field stays 'ai').
 */
function readWrittenValue(
  asyncRec: Record<string, unknown> | undefined,
  inlineRec: Record<string, unknown> | undefined,
  slugRec: Record<string, unknown> | undefined,
  field: string,
): string | undefined {
  const asyncFields = isRecord(asyncRec?.fields)
    ? (asyncRec!.fields as Record<string, unknown>)
    : undefined;
  if (asyncFields && field in asyncFields) return normalizeScalar(asyncFields[field]);
  const inlineFields = isRecord(inlineRec?.fields)
    ? (inlineRec!.fields as Record<string, unknown>)
    : undefined;
  if (inlineFields && field in inlineFields) return normalizeScalar(inlineFields[field]);
  // Family F (url_slug) writes its own top-level postgen_slug.fields — see
  // the slugRec reader in readPostgenEvidence.
  const slugFields = isRecord(slugRec?.fields)
    ? (slugRec!.fields as Record<string, unknown>)
    : undefined;
  if (slugFields && field in slugFields) return normalizeScalar(slugFields[field]);
  if (asyncRec && field in asyncRec) return normalizeScalar(asyncRec[field]);
  if (inlineRec && field in inlineRec) return normalizeScalar(inlineRec[field]);
  if (slugRec && field in slugRec) return normalizeScalar(slugRec[field]);
  return undefined;
}

/** Read the field's CURRENT value off ContentDetail (only for drift-checkable fields). */
function readCurrentValue(content: ContentDetail, field: string): string | undefined {
  const key = DRIFT_CHECKABLE[field];
  if (!key) return undefined;
  const v = (content as unknown as Record<string, unknown>)[key];
  return normalizeScalar(v);
}

/**
 * Parse the post-generation evidence already on the wire into a per-field
 * summary the rail can render. Never throws — returns EMPTY_SUMMARY on any
 * missing/malformed input.
 */
export function readPostgenEvidence(content: ContentDetail | undefined): MetadataRunSummary {
  if (!content) return EMPTY_SUMMARY;

  const topStatus: RunStatus = readStatus(content.metadata_generation_status);
  const gmRaw: unknown = content.generation_metadata;
  if (!isRecord(gmRaw)) {
    return { ...EMPTY_SUMMARY, status: topStatus };
  }

  // generation_metadata is `{qa_noindex?, postgen_metadata_async?} & Record<string, unknown>`.
  // postgen_metadata_async is partly typed (state / skipped_user_edited / skipped_frame_capped)
  // but its per_field / baseline_metadata extensions, and the entire postgen_metadata /
  // postgen_social siblings, are untyped — read everything through `unknown` + guards.
  const asyncTyped = gmRaw.postgen_metadata_async;
  const asyncExtras = (asyncTyped ?? undefined) as unknown;
  const asyncRec = isRecord(asyncExtras) ? asyncExtras : undefined;

  const inlineRaw: unknown = (gmRaw as Record<string, unknown>).postgen_metadata;
  const inlineRec = isRecord(inlineRaw) ? inlineRaw : undefined;

  const socialRaw: unknown = (gmRaw as Record<string, unknown>).postgen_social;
  const socialRec = isRecord(socialRaw) ? socialRaw : undefined;

  // Family F (url_slug) writes its OWN top-level key, mirroring postgen_social
  // rather than postgen_metadata_async/postgen_metadata — postgen_slug.py's
  // _persist_evidence hard-SETs the whole sub-object at its key, so sharing
  // Family A's key would clobber its title/meta_title/meta_description blob.
  const slugRaw: unknown = (gmRaw as Record<string, unknown>).postgen_slug;
  const slugRec = isRecord(slugRaw) ? slugRaw : undefined;

  // Status: async.state wins (most authoritative), then content.metadata_generation_status.
  const status: RunStatus =
    readStatus(asyncRec?.state) ?? topStatus;

  // skipped_user_edited — Family A: async.skipped_user_edited OR inline.skipped_user_edited.
  // Family B (og_*, twitter_*): handled separately below (postgen_social.edited is a
  // PACKET-level boolean, not a per-field set — see the Family B honesty rule).
  const skippedAsync = truthyFieldSet(asyncRec?.skipped_user_edited);
  const skippedInline = truthyFieldSet(inlineRec?.skipped_user_edited);

  // postgen_social.edited is a PACKET-level boolean (content_service stamps
  // social_packet["edited"]=True on ANY social edit). Once true, per-field B
  // attribution is unknowable because the editor writes INTO postgen_social.fields,
  // destroying the AI original — handled per-field in the loop below.
  const socialPacketEdited = socialRec?.edited === true;

  // per_field — async wins per-field, fall back to inline (Family A), then
  // social (Family B), then slug (Family F).
  const perFieldAsync = perFieldMap(asyncRec?.per_field);
  const perFieldInline = perFieldMap(inlineRec?.per_field);
  const perFieldSocial = perFieldMap(socialRec?.per_field);
  const perFieldSlug = perFieldMap(slugRec?.per_field);

  // baseline_metadata — core fields; async wins, fall back to inline. Family F
  // stamps its own baseline under postgen_slug.field_baseline (same shape,
  // build_postgen_evidence_blob's field_baseline key) so a one-click revert
  // can restore the pre-run slug.
  const baselineAsyncRec = isRecord(asyncRec?.baseline_metadata)
    ? (asyncRec!.baseline_metadata as Record<string, unknown>)
    : undefined;
  const baselineInlineRec = isRecord(inlineRec?.baseline_metadata)
    ? (inlineRec!.baseline_metadata as Record<string, unknown>)
    : undefined;
  const baselineSlugRec = isRecord(slugRec?.field_baseline)
    ? (slugRec!.field_baseline as Record<string, unknown>)
    : undefined;

  const isFailedRun = status === 'failed';

  const fieldStates: Record<string, MetadataFieldState> = {};
  const updatedFields: string[] = [];
  const keptFields: string[] = [];
  let needsReviewCount = 0;

  for (const field of POSTGEN_FIELDS) {
    const skippedAsyncHas = skippedAsync.has(field);
    const skipped = skippedAsyncHas || skippedInline.has(field);
    const isFamilyB = FAMILY_B_FIELDS.has(field);

    // ── Family B honesty rule ──────────────────────────────────────────────
    // When the social packet has been edited at all, per-field attribution for
    // og_*/twitter_* is UNKNOWABLE — the editor writes into postgen_social.fields
    // and overwrites the AI original. Omit B fields entirely (no chip) UNLESS
    // async.skipped_user_edited explicitly covers them (that stays authoritative
    // because the run itself acknowledged the pre-edit). The "original-stash"
    // second-slice backend item will restore per-field B attribution later.
    if (isFamilyB && socialPacketEdited && !skippedAsyncHas) {
      continue;
    }

    const perField =
      perFieldAsync.get(field) ??
      perFieldInline.get(field) ??
      perFieldSocial.get(field) ??
      perFieldSlug.get(field);

    let source: FieldSource;
    let needsReview: boolean;

    if (skipped) {
      source = 'user-edited';
      needsReview = false;
    } else if (perField?.fallback_fired || (isFailedRun && perField !== undefined)) {
      source = 'fallback';
      needsReview = true;
    } else if (perField?.applied) {
      // ── Drift rule (Family A + async, and Family F/slug) ─────────────────
      // Before labeling 'ai', compare the value the run WROTE against the
      // field's CURRENT value on ContentDetail. If the human rewrote the field
      // post-run, the chip must label the human's text "Your edit" — never "AI".
      // If the evidence carries no written-value record, keep 'ai' as today.
      const written = readWrittenValue(asyncRec, inlineRec, slugRec, field);
      const current = readCurrentValue(content, field);
      if (
        written !== undefined &&
        written.trim() !== '' &&
        current !== undefined &&
        current.trim() !== '' &&
        written.trim() !== current.trim()
      ) {
        source = 'user-edited';
        needsReview = false;
      } else {
        source = 'ai';
        needsReview = false;
      }
    } else {
      // No evidence for this field — chip renders nothing; omit from fieldStates.
      continue;
    }

    const baselineValue = baselineAsyncRec
      ? baselineString(baselineAsyncRec, field)
      : baselineInlineRec
        ? baselineString(baselineInlineRec, field)
        : baselineSlugRec
          ? baselineString(baselineSlugRec, field)
          : undefined;

    const state: MetadataFieldState = {
      field,
      source,
      needsReview,
    };
    if (baselineValue !== undefined) state.baseline = baselineValue;

    fieldStates[field] = state;

    if (source === 'user-edited') {
      keptFields.push(field);
    } else {
      updatedFields.push(field);
    }
    if (needsReview) needsReviewCount += 1;
  }

  return {
    status,
    needsReviewCount,
    updatedFields,
    keptFields,
    fieldStates,
  };
}
